import { Chart } from 'chart.js';
import { useRef, useEffect } from 'react';
import {
  formatShortDate,
  durationToString,
  msToHours,
  hoursToMs,
  median,
  average as avg,
} from '#utils';

const defaultFontColor = '#CCC';

const MAX_RADIUS = 50;
const TIME_TO_REVIEW_TITLE = 'Median time to review';

const datasetConfig = {
  pointHoverRadius: 5,
  pointHitRadius: 10,
  borderWidth: 2,
  lineTension: 0,
};

const timeToRadius = (time, maxValue) => MAX_RADIUS * (time / maxValue);

const getBubbleDataset = (reviewers, maxToReview) => ({
  ...datasetConfig,
  data: reviewers.map((r) => ({
    x: r.totalComments,
    y: r.totalReviews,
    r: timeToRadius(r.timeToReview, maxToReview),
  })),
  label: 'Effort',
  order: 0,
  borderColor: 'rgba(252, 170, 15, 1)',
  backgroundColor: 'rgba(252, 170, 15, 0.2)',
  fill: 'origin',
});

const getTooltipOptions = () => ({
  mode: 'x',
  position: 'nearest',
  backgroundColor: 'rgba(0, 0, 0, 0.9)',
  intersect: false,
  titleSpacing: 6,
  xPadding: 8,
  yPadding: 8,
  callbacks: {
    label: (item, data) => {
      const title = data.datasets[item.datasetIndex].label;
      if (title === TIME_TO_REVIEW_TITLE) return ` ${title}: ${durationToString(hoursToMs(item.yLabel))}`;
      return ` ${title}: ${item.yLabel}`;
    },
    labelColor: (item, chart) => {
      const { borderColor } = chart.config.data.datasets[item.datasetIndex];
      return { borderColor, backgroundColor: borderColor };
    },
  },
});

const getYAxisOptions = () => ({
  id: 'y-axis',
  type: 'linear',
  position: 'left',
  ticks: {
    min: 0,
  },
  gridLines: {
    color: 'rgba(128, 128, 128, 0.1)',
    lineWidth: 1,
  },
});

const getXAxisOptions = () => ({
  id: 'x-axis',
  type: 'linear',
  ticks: {
    autoSkipPadding: 10,
  },
  gridLines: {
    display: false,
  },
});

const getMaxTimeToReview = (reviewers) => Math.max(...reviewers.map((r) => r.timeToReview));

export const useReviewTimeChart = (reviewers) => {
  const chartRef = useRef();
  const hasRef = !chartRef.current;
  const maxToReview = getMaxTimeToReview(reviewers);

  useEffect(() => {
    if (!chartRef.current) return;

    const labels = reviewers.map((r) => r.name);

    const options = {
      responsive: true,
      maintainAspectRatio: false,
      layout: {
        padding: 16,
      },
      hover: {
        mode: 'x',
        intersect: false,
      },
      tooltips: getTooltipOptions(),
      scales: {
        yAxes: [getYAxisOptions()],
        xAxes: [getXAxisOptions()],
      },
    };

    Chart.defaults.defaultFontColor = defaultFontColor;

    new Chart(chartRef.current, {
      type: 'bubble',
      data: {
        labels,
        datasets: [
          getBubbleDataset(reviewers, maxToReview),
        ],
      },
      options,
    });
  }, [hasRef, reviewers, maxToReview]);

  return { chartRef };
};
